import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useOperators } from './operators';
import { useMapGetter } from 'dashboard/composables/store.js';
import {
  buildAttributesFilterTypes,
  CONTACT_ATTRIBUTES,
} from './helper/filterHelper.js';
import countries from 'shared/constants/countries.js';
import ConversionsAPI from 'dashboard/api/conversions';

// Lista de conversoes com o NOME da tela de Conversoes (display_name) — as
// regras "Conversao" e "Abandono" escolhem daqui em vez de pedir o codigo
// tecnico digitado. Carrega uma vez por sessao da pagina.
const conversoes = ref([]);
let conversoesPedidas = false;
const carregarConversoes = () => {
  if (conversoesPedidas) return;
  conversoesPedidas = true;
  ConversionsAPI.get()
    .then(({ data }) => {
      conversoes.value = (data?.payload || []).map(c => ({
        id: c.identifier,
        name: c.name || c.identifier,
      }));
    })
    .catch(() => {
      conversoesPedidas = false;
    });
};

/**
 * @typedef {Object} FilterOption
 * @property {string|number} id
 * @property {string} name
 * @property {import('vue').VNode} [icon]
 */

/**
 * @typedef {Object} FilterOperator
 * @property {string} value
 * @property {string} label
 * @property {string} icon
 * @property {boolean} hasInput
 */

/**
 * @typedef {Object} FilterType
 * @property {string} attributeKey - The attribute key
 * @property {string} value - This is a proxy for the attribute key used in FilterSelect
 * @property {string} attributeName - The attribute name used to display on the UI
 * @property {string} label - This is a proxy for the attribute name used in FilterSelect
 * @property {'multiSelect'|'searchSelect'|'plainText'|'date'|'booleanSelect'} inputType - The input type for the attribute
 * @property {FilterOption[]} [options] - The options available for the attribute if it is a multiSelect or singleSelect type
 * @property {'text'|'number'} dataType
 * @property {FilterOperator[]} filterOperators - The operators available for the attribute
 * @property {'standard'|'additional'|'customAttributes'} attributeModel
 */

/**
 * @typedef {Object} FilterGroup
 * @property {string} name
 * @property {FilterType[]} attributes
 */

/**
 * Composable that provides conversation filtering context
 * @returns {{ filterTypes: import('vue').ComputedRef<FilterType[]>, filterGroups: import('vue').ComputedRef<FilterGroup[]> }}
 */
export function useContactFilterContext() {
  const { t } = useI18n();

  const store = useStore();
  // Garante que as fases/pipelines estejam carregadas para as regras de negócio.
  store.dispatch('pipelines/get');

  const contactAttributes = useMapGetter('attributes/getContactAttributes');
  const labels = useMapGetter('labels/getLabels');
  const segments = useMapGetter('segments/getSegments');
  const pipelines = useMapGetter('pipelines/getPipelines');
  const inboxes = useMapGetter('inboxes/getInboxes');
  const forms = useMapGetter('forms/getAll');
  carregarConversoes();
  if (!(forms.value || []).length) store.dispatch('forms/get');

  // Formulario enviado: o envio grava o embed_token do formulario.
  const formOptions = computed(() =>
    (forms.value || []).map(f => ({ id: f.embed_token, name: f.name }))
  );

  // Apenas Listas estáticas podem ser usadas na regra "Pertence à lista".
  const contactLists = computed(() =>
    (segments.value || [])
      .filter(s => s.segment_type === 'list')
      .map(s => ({ id: s.id, name: s.name }))
  );

  // Fases de funil (deduplicadas por key) — o valor enviado é a `key`, que é o
  // que fica gravado em deals.funnel_stage.
  const dealStageOptions = computed(() => {
    const seen = new Set();
    const out = [];
    (pipelines.value || []).forEach(pipeline => {
      const raw = pipeline.stages;
      const stages = Array.isArray(raw) ? raw : Object.values(raw || {});
      stages.forEach(stage => {
        const key = stage?.key;
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push({ id: key, name: stage.name || key });
      });
    });
    return out;
  });

  const pipelineOptions = computed(() =>
    (pipelines.value || []).map(pipeline => ({
      id: pipeline.id,
      name: pipeline.name,
    }))
  );

  const inboxOptions = computed(() =>
    (inboxes.value || []).map(inbox => ({ id: inbox.id, name: inbox.name }))
  );

  const {
    operators,
    equalityOperators,
    presenceOperators,
    containmentOperators,
    dateOperators,
    getOperatorTypes,
  } = useOperators();

  const dealStatusOptions = computed(() => [
    { id: 'open', name: t('CONTACTS_LAYOUT.FILTER.DEAL_STATUS_OPEN') },
    { id: 'won', name: t('CONTACTS_LAYOUT.FILTER.DEAL_STATUS_WON') },
    { id: 'lost', name: t('CONTACTS_LAYOUT.FILTER.DEAL_STATUS_LOST') },
  ]);

  const optOutOptions = computed(() => [
    { id: 'true', name: t('CONTACTS_LAYOUT.FILTER.EMAIL_OPT_OUT_TRUE') },
    { id: 'false', name: t('CONTACTS_LAYOUT.FILTER.EMAIL_OPT_OUT_FALSE') },
  ]);

  // Operadores numéricos (>, <) para o valor do negócio.
  const numericComparisonOperators = computed(() => [
    operators.value.is_greater_than,
    operators.value.is_less_than,
  ]);

  // Operadores de presença (tem / não tem) para "Tem negócio".
  const hasDealOperators = computed(() => [
    operators.value.is_present,
    operators.value.is_not_present,
  ]);

  /**
   * @type {import('vue').ComputedRef<FilterType[]>}
   */
  const customFilterTypes = computed(() =>
    buildAttributesFilterTypes(
      contactAttributes.value,
      getOperatorTypes,
      'contact'
    )
  );

  /**
   * @type {import('vue').ComputedRef<FilterType[]>}
   */
  const filterTypes = computed(() => [
    {
      attributeKey: CONTACT_ATTRIBUTES.NAME,
      value: CONTACT_ATTRIBUTES.NAME,
      attributeName: t('CONTACTS_LAYOUT.FILTER.NAME'),
      label: t('CONTACTS_LAYOUT.FILTER.NAME'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.EMAIL,
      value: CONTACT_ATTRIBUTES.EMAIL,
      attributeName: t('CONTACTS_LAYOUT.FILTER.EMAIL'),
      label: t('CONTACTS_LAYOUT.FILTER.EMAIL'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.PHONE_NUMBER,
      value: CONTACT_ATTRIBUTES.PHONE_NUMBER,
      attributeName: t('CONTACTS_LAYOUT.FILTER.PHONE_NUMBER'),
      label: t('CONTACTS_LAYOUT.FILTER.PHONE_NUMBER'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.IDENTIFIER,
      value: CONTACT_ATTRIBUTES.IDENTIFIER,
      attributeName: t('CONTACTS_LAYOUT.FILTER.IDENTIFIER'),
      label: t('CONTACTS_LAYOUT.FILTER.IDENTIFIER'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.COUNTRY_CODE,
      value: CONTACT_ATTRIBUTES.COUNTRY_CODE,
      attributeName: t('FILTER.ATTRIBUTES.COUNTRY_NAME'),
      label: t('FILTER.ATTRIBUTES.COUNTRY_NAME'),
      inputType: 'searchSelect',
      options: countries,
      dataType: 'number',
      filterOperators: equalityOperators.value,
      attributeModel: 'additional',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.CITY,
      value: CONTACT_ATTRIBUTES.CITY,
      attributeName: t('CONTACTS_LAYOUT.FILTER.CITY'),
      label: t('CONTACTS_LAYOUT.FILTER.CITY'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.CREATED_AT,
      value: CONTACT_ATTRIBUTES.CREATED_AT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.CREATED_AT'),
      label: t('CONTACTS_LAYOUT.FILTER.CREATED_AT'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.LAST_ACTIVITY_AT,
      value: CONTACT_ATTRIBUTES.LAST_ACTIVITY_AT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.LAST_ACTIVITY'),
      label: t('CONTACTS_LAYOUT.FILTER.LAST_ACTIVITY'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.REFERER,
      value: CONTACT_ATTRIBUTES.REFERER,
      attributeName: t('CONTACTS_LAYOUT.FILTER.REFERER_LINK'),
      label: t('CONTACTS_LAYOUT.FILTER.REFERER_LINK'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.BLOCKED,
      value: CONTACT_ATTRIBUTES.BLOCKED,
      attributeName: t('CONTACTS_LAYOUT.FILTER.BLOCKED'),
      label: t('CONTACTS_LAYOUT.FILTER.BLOCKED'),
      inputType: 'searchSelect',
      options: [
        {
          id: 'true',
          name: t('CONTACTS_LAYOUT.FILTER.BLOCKED_TRUE'),
        },
        {
          id: 'false',
          name: t('CONTACTS_LAYOUT.FILTER.BLOCKED_FALSE'),
        },
      ],
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.LABELS,
      value: CONTACT_ATTRIBUTES.LABELS,
      attributeName: t('CONTACTS_FILTER.ATTRIBUTES.LABELS'),
      label: t('CONTACTS_FILTER.ATTRIBUTES.LABELS'),
      inputType: 'multiSelect',
      options: labels.value?.map(label => ({
        id: label.title,
        name: label.title,
      })),
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.LIFECYCLE_STAGE,
      value: CONTACT_ATTRIBUTES.LIFECYCLE_STAGE,
      attributeName: t('CONTACTS_LAYOUT.FILTER.LIFECYCLE_STAGE'),
      label: t('CONTACTS_LAYOUT.FILTER.LIFECYCLE_STAGE'),
      inputType: 'searchSelect',
      options: [
        { id: 'lead', name: 'Lead' },
        { id: 'qualificado', name: 'Qualificado' },
        { id: 'negociacao', name: 'Negociação' },
        { id: 'cliente', name: 'Cliente' },
        { id: 'inativo', name: 'Inativo' },
      ],
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.COMPANY_ID,
      value: CONTACT_ATTRIBUTES.COMPANY_ID,
      attributeName: t('CONTACTS_LAYOUT.FILTER.COMPANY'),
      label: t('CONTACTS_LAYOUT.FILTER.COMPANY'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: presenceOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.UTM_SOURCE,
      value: CONTACT_ATTRIBUTES.UTM_SOURCE,
      attributeName: t('CONTACTS_LAYOUT.FILTER.UTM_SOURCE'),
      label: t('CONTACTS_LAYOUT.FILTER.UTM_SOURCE'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.UTM_MEDIUM,
      value: CONTACT_ATTRIBUTES.UTM_MEDIUM,
      attributeName: t('CONTACTS_LAYOUT.FILTER.UTM_MEDIUM'),
      label: t('CONTACTS_LAYOUT.FILTER.UTM_MEDIUM'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.UTM_CAMPAIGN,
      value: CONTACT_ATTRIBUTES.UTM_CAMPAIGN,
      attributeName: t('CONTACTS_LAYOUT.FILTER.UTM_CAMPAIGN'),
      label: t('CONTACTS_LAYOUT.FILTER.UTM_CAMPAIGN'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.HAS_CONVERSION,
      value: CONTACT_ATTRIBUTES.HAS_CONVERSION,
      attributeName: t('CONTACTS_LAYOUT.FILTER.HAS_CONVERSION'),
      label: t('CONTACTS_LAYOUT.FILTER.HAS_CONVERSION'),
      inputType: 'multiSelect',
      options: conversoes.value,
      dataType: 'text',
      filterOperators: presenceOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.HAS_ABANDONMENT,
      value: CONTACT_ATTRIBUTES.HAS_ABANDONMENT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.HAS_ABANDONMENT'),
      label: t('CONTACTS_LAYOUT.FILTER.HAS_ABANDONMENT'),
      inputType: 'multiSelect',
      options: conversoes.value,
      dataType: 'text',
      filterOperators: presenceOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.HAS_FORM_SUBMISSION,
      value: CONTACT_ATTRIBUTES.HAS_FORM_SUBMISSION,
      attributeName: t('CONTACTS_LAYOUT.FILTER.HAS_FORM_SUBMISSION'),
      label: t('CONTACTS_LAYOUT.FILTER.HAS_FORM_SUBMISSION'),
      inputType: 'multiSelect',
      options: formOptions.value,
      dataType: 'text',
      filterOperators: presenceOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.SEGMENT_LIST,
      value: CONTACT_ATTRIBUTES.SEGMENT_LIST,
      attributeName: t('CONTACTS_LAYOUT.FILTER.SEGMENT_LIST'),
      label: t('CONTACTS_LAYOUT.FILTER.SEGMENT_LIST'),
      inputType: 'multiSelect',
      options: contactLists.value,
      dataType: 'number',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.DEAL_STAGE,
      value: CONTACT_ATTRIBUTES.DEAL_STAGE,
      attributeName: t('CONTACTS_LAYOUT.FILTER.DEAL_STAGE'),
      label: t('CONTACTS_LAYOUT.FILTER.DEAL_STAGE'),
      inputType: 'multiSelect',
      options: dealStageOptions.value,
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.DEAL_STATUS,
      value: CONTACT_ATTRIBUTES.DEAL_STATUS,
      attributeName: t('CONTACTS_LAYOUT.FILTER.DEAL_STATUS'),
      label: t('CONTACTS_LAYOUT.FILTER.DEAL_STATUS'),
      inputType: 'multiSelect',
      options: dealStatusOptions.value,
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.DEAL_PIPELINE,
      value: CONTACT_ATTRIBUTES.DEAL_PIPELINE,
      attributeName: t('CONTACTS_LAYOUT.FILTER.DEAL_PIPELINE'),
      label: t('CONTACTS_LAYOUT.FILTER.DEAL_PIPELINE'),
      inputType: 'multiSelect',
      options: pipelineOptions.value,
      dataType: 'number',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.DEAL_VALUE,
      value: CONTACT_ATTRIBUTES.DEAL_VALUE,
      attributeName: t('CONTACTS_LAYOUT.FILTER.DEAL_VALUE'),
      label: t('CONTACTS_LAYOUT.FILTER.DEAL_VALUE'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: numericComparisonOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.HAS_DEAL,
      value: CONTACT_ATTRIBUTES.HAS_DEAL,
      attributeName: t('CONTACTS_LAYOUT.FILTER.HAS_DEAL'),
      label: t('CONTACTS_LAYOUT.FILTER.HAS_DEAL'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: hasDealOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.CONVERSATION_INBOX,
      value: CONTACT_ATTRIBUTES.CONVERSATION_INBOX,
      attributeName: t('CONTACTS_LAYOUT.FILTER.CONVERSATION_INBOX'),
      label: t('CONTACTS_LAYOUT.FILTER.CONVERSATION_INBOX'),
      inputType: 'multiSelect',
      options: inboxOptions.value,
      dataType: 'number',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.EMAIL_OPT_OUT,
      value: CONTACT_ATTRIBUTES.EMAIL_OPT_OUT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.EMAIL_OPT_OUT'),
      label: t('CONTACTS_LAYOUT.FILTER.EMAIL_OPT_OUT'),
      inputType: 'searchSelect',
      options: optOutOptions.value,
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    ...customFilterTypes.value,
  ]);

  return { filterTypes };
}
